import GroupChevron from '../GroupChevron';
import CellCheckbox from '../CellCheckbox';
import CellFavorite from '../CellFavorite';
import Children from './Children';
import { createPopper } from '@popperjs/core';

let hoveredRowId;

function addHoverClass(el) {
    const rows = document.querySelectorAll('.ag-row--action-hover');
    for (let row of rows) {
        row.classList?.remove('ag-row--action-hover');
    }
    const rowsHovered = el.querySelectorAll(`[row-id='${hoveredRowId}']`);
    for (let row of rowsHovered) {
        row.classList?.add('ag-row--action-hover');
    }
}

export default function BaseCell(cell, params) {
    if (!params) return null;
    const baseCell = document.createElement('div');
    baseCell.classList.add('w5-cell');
    if (params?.node?.id) baseCell.setAttribute('data-rowid', params.node.id);
    const data = params.data;

    if (params.customStyles) {
        Object.keys(params.customStyles).forEach((current) => {
            baseCell.style[current] = params.customStyles[current];
        });
    }

    // Important the optional chaining because if it's a children row, it has no colDef
    // and it will break
    if (params?.colDef?.rowDrag) baseCell.classList.add('w5-cell-drag');

    if (
        params.context?.useDragCustom &&
        params.column &&
        params.columnApi &&
        ((params.context?.canDrag && params.context?.canDrag(data)) || !params.context?.canDrag)
    ) {
        const colDef = params.column.colDef;
        const columns = params.columnApi.getColumns();
        if (colDef.colId === columns[0].colId) {
            const customDragger = document.createElement('div');
            customDragger.classList.add('w5-cell__custom-dragger');
            const dragger = `<svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                                <path
                                    fill-rule="evenodd"
                                    clip-rule="evenodd"
                                    d="M0 18c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2zm4-6c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm0-6c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm1 12c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2zm4-6c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm0-6c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2z" fill="var(--actionMinor-400)" />
                            </svg>`;
            customDragger.innerHTML = dragger;
            baseCell.appendChild(customDragger);
        }
    }
    addCellClasses(baseCell, params);
    addChildrenCellClasses(baseCell, params);

    if (params?.checkbox?.onClick) {
        const checkbox = CellCheckbox(params);
        checkbox && baseCell.appendChild(checkbox);
    }

    if (params?.favorite?.onClick) {
        const favorite = CellFavorite(params);
        favorite && baseCell.appendChild(favorite);
    }

    if (params?.withGroup) {
        const chevron = GroupChevron(params);
        chevron && baseCell.appendChild(chevron);
    }

    let finalCell;
    if (params?.data?.isFullWidth) finalCell = cell;
    else if (params?.childrenParams && params?.data?.isChildren) {
        finalCell = Children(params);
        if (params?.withGroup) {
            finalCell.classList.add('w5-cell__children-first-cell');
        } else {
            finalCell.classList.add('w5-cell__children');
        }
    } else finalCell = cell;

    addCellOnClick(baseCell, params);
    addChildrenCellOnClick(baseCell, params);
    addNonClickCellHoverLink(baseCell, params);

    if (params?.getUrl) {
        let linkWrapper;
        if (data?.isChildren) {
            linkWrapper = addChildrenLinkWrapper(finalCell, params, data);
            if (linkWrapper) finalCell = linkWrapper;
        } else {
            linkWrapper = addLinkWrapper(finalCell, params, data);
            if (linkWrapper) finalCell = linkWrapper;
        }
    }

    if (finalCell) baseCell.appendChild(finalCell);

    function isValidValue(value) {
        if (value === undefined || value === null || value === '') return false;
        return true;
    }

    if (
        params.useTooltip &&
        ((params.tooltipKey && isValidValue(data?.[params.tooltipKey])) ||
            (!params.tooltipKey && isValidValue(params.value)))
    ) {
        const element = params?.context?.tableId
            ? document.querySelector(`#${params?.context?.tableId}`)
            : document;
        const tooltip = element.querySelector('.w5-tooltip');
        let popperInstance = null;

        baseCell.addEventListener('mouseover', () => {
            tooltip.style.display = 'block';
            tooltip.firstElementChild.innerText =
                (params?.tooltipValue && params?.tooltipValue(params.value)) ||
                data[params.tooltipKey] ||
                params.value;
            let placement = 'top-start';
            if (params.align === 'right') placement = 'top-end';
            if (params.align === 'center') placement = 'top';

            createPopper(baseCell, tooltip, {
                placement,
                modifiers: [
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 5],
                        },
                    },
                ],
            });
        });

        baseCell.addEventListener('mouseout', () => {
            tooltip.style.display = 'none';
            if (popperInstance) {
                popperInstance.destroy();
                popperInstance = null;
            }
        });
    }

    if (params?.context?.hasActions && !params?.data?.isFullWidth) {
        controlActionsDisplay(baseCell, params);
    }

    return baseCell;
}

function addCellClasses(baseCell, params) {
    if (!params?.data?.isChildren || (params?.data?.isChildren && !params?.childrenParams)) {
        if (params?.bold) baseCell.classList.add('w5-cell--bold');
        if (params?.link) baseCell.classList.add('w5-cell--link');
        if (params?.align) baseCell.classList.add(`w5-cell--align-${params.align}`);
    }
}

function addChildrenCellClasses(baseCell, params) {
    if (params?.data?.isChildren) {
        if (params?.childrenParams?.bold) baseCell.classList.add('w5-cell--bold');
        if (params?.childrenParams?.link) baseCell.classList.add('w5-cell--link');
        if (params?.childrenParams?.align)
            baseCell.classList.add(`w5-cell--align-${params.childrenParams.align}`);
    }
}

function addCellOnClick(baseCell, params) {
    if (
        (!params?.getUrl || (params?.getUrl && params?.forceUrl)) &&
        baseCell &&
        params?.onClick &&
        !params?.data?.isChildren
    ) {
        baseCell.classList.add('w5-cell--click');
        if (params.hoverLink) {
            baseCell.classList.add('w5-cell-click--with-hover');
        }
        baseCell.addEventListener('click', (event) => {
            event.stopPropagation();
            if (params?.forceUrl) event.preventDefault();
            params.onClick(params);
        });
    }
}

function addChildrenCellOnClick(baseCell, params) {
    if (
        !params?.getUrl &&
        baseCell &&
        params?.childrenParams?.onClick &&
        params?.data?.isChildren
    ) {
        baseCell.classList.add('w5-cell--click');
        if (params.hoverLink) {
            baseCell.classList.add('w5-cell-click--with-hover');
        }
        baseCell.addEventListener('click', (event) => {
            event.stopPropagation();
            params.childrenParams.onClick(params);
        });
    }
}

function addNonClickCellHoverLink(baseCell, params) {
    if (params.hoverLink && baseCell && !params?.onClick && !params?.childrenParams?.onClick) {
        baseCell.classList.add('w5-cell--non-click-with-hover');
    }
}

function addLinkWrapper(finalCell, params, data) {
    if (
        (!params?.onClick || (params?.onClick && params?.forceUrl)) &&
        finalCell &&
        params?.getUrl &&
        data
    ) {
        const linkWrapper = document.createElement('a');
        linkWrapper.classList.add('w5-cell-link-wrapper');
        if (params.hoverLink) {
            linkWrapper.classList.add('w5-cell-link-wrapper--with-hover');
        }
        const url = params.getUrl(data, params.value);
        if (!url) return null;
        linkWrapper.href = url;
        linkWrapper.appendChild(finalCell);
        return linkWrapper;
    }
}

function addChildrenLinkWrapper(finalCell, params, data) {
    if (
        (!params?.onClick || (params?.onClick && params?.forceUrl)) &&
        finalCell &&
        params?.getUrl &&
        data
    ) {
        const linkWrapper = document.createElement('a');
        linkWrapper.classList.add('w5-cell-link-wrapper');
        if (params.hoverLink) {
            linkWrapper.classList.add('w5-cell-link-wrapper--with-hover');
        }
        const url = params.getUrl(data, params.value);
        if (!url) return null;
        linkWrapper.href = url;
        linkWrapper.appendChild(finalCell);
        return linkWrapper;
    }
}

function controlActionsDisplay(baseCell, params) {
    const node = params.node;
    const tableId = params?.context?.tableId || '';
    const elementId = tableId ? `w5-actions-component-${tableId}` : 'w5-actions-component';

    function getBodyViewPort() {
        const tableDiv = params?.context?.tableRef?.current?.eGridDiv || null;

        let bodyViewport = params.api.gridBodyCtrl.eBodyViewport;
        if (tableDiv) {
            bodyViewport = tableDiv.querySelector('.ag-body-viewport');
        }
        return bodyViewport;
    }

    const actionsWrapper = document.getElementById(elementId);

    baseCell.addEventListener('mouseover', () => {
        const bodyViewport = getBodyViewPort();
        const bodyViewportContent = bodyViewport.children;
        let bodyViewportContentChild;

        for (let current of bodyViewportContent) {
            if (current.classList.contains('ag-center-cols-clipper')) {
                bodyViewportContentChild = current;
            }
        }

        // We need to inform about that to add the correct style to prevent overlap the scroll bar.
        const hasScroll = bodyViewportContentChild.offsetHeight > bodyViewport.offsetHeight;
        params.context.getHoveredRowInfo({
            data: params.data,
            hasScroll,
            rowElement: params?.eGridCell?.offsetParent,
        });

        hoveredRowId = node.id;

        const scrollTop = bodyViewport.scrollTop;
        const centerElement = 40 - scrollTop;
        const actionsTop = node.rowTop + centerElement;

        if (actionsWrapper) {
            actionsWrapper.style.display = 'block';
            actionsWrapper.style.top = `${actionsTop + 3}px`;
            actionsWrapper.style.height = `${node.rowHeight - 2}px`;
            bodyViewport.onscroll = () => {
                actionsWrapper.style.display = 'none';
            };
        }
    });

    // need all this eventListeners because some overlap the others
    // >> when I'm out from one, can be over the other, and if we don't add
    // >> this three eventListeners may cause undesired flickering in some cases
    baseCell.addEventListener('mouseout', () => {
        if (actionsWrapper) {
            actionsWrapper.style.display = 'none';
        }
    });

    actionsWrapper?.addEventListener('mouseover', () => {
        if (actionsWrapper) {
            addHoverClass(getBodyViewPort());
            actionsWrapper.style.display = 'block';
        }
        params.context.possibleScrolledRow = node.id;
    });

    actionsWrapper?.addEventListener('mouseout', () => {
        if (actionsWrapper) {
            actionsWrapper.style.display = 'none';
        }
        if (params?.context?.possibleScrolledRow === node.id) {
            actionsWrapper?.scrollTo(0, 0);
            params.context.possibleScrolledRow = '';
        }
    });

    if (actionsWrapper) {
        const wheelEvent = 'onWheel' in actionsWrapper ? 'wheel' : 'mousewheel';
        actionsWrapper?.addEventListener(wheelEvent, function (event) {
            if (params?.context?.possibleScrolledRow === node.id) {
                const bodyViewport = getBodyViewPort();
                bodyViewport.scrollBy(event.deltaX, event.deltaY);
                if (actionsWrapper) {
                    actionsWrapper.style.display = 'none';
                }
            }
        });
    }
}
